import { useEffect, useRef } from 'react'
import { ScrollArea } from '@journ/ui/scroll-area'
import { useIntlayer } from 'react-intlayer'
import { useDateFormat } from '@/features/preferences/hooks/use-date-format'
import type { Message } from '../domain/chat'
import { MessageBubble } from './message-bubble'

type MessageThreadProps = {
	messages: Message[]
	currentUserId: string
}

const shouldCluster = (a: Message | undefined, b: Message | undefined): boolean => {
	if (!a || !b) return false
	if (a.from !== b.from) return false
	const delta = new Date(b.createdAt).getTime() - new Date(a.createdAt).getTime()
	return delta < 5 * 60 * 1000
}

const toDateKey = (iso: string): string => {
	const date = new Date(iso)
	if (Number.isNaN(date.getTime())) return iso
	return date.toISOString().slice(0, 10)
}

const MessageThread = ({ messages, currentUserId }: MessageThreadProps) => {
	const content = useIntlayer('chat')
	const { formatPreferenceDate } = useDateFormat()
	const bottomRef = useRef<HTMLDivElement>(null)
	const lastCountRef = useRef(messages.length)

	useEffect(() => {
		const grew = messages.length > lastCountRef.current
		lastCountRef.current = messages.length
		if (grew) {
			bottomRef.current?.scrollIntoView({ behavior: 'smooth' })
		}
	}, [messages])

	if (messages.length === 0) {
		return (
			<div className='flex flex-1 flex-col items-center justify-center gap-2 px-4 text-center'>
				<p className='text-sm text-muted-foreground'>{content.emptyThread}</p>
			</div>
		)
	}

	const todayKey = toDateKey(new Date().toISOString())
	const yesterdayDate = new Date()
	yesterdayDate.setDate(yesterdayDate.getDate() - 1)
	const yesterdayKey = toDateKey(yesterdayDate.toISOString())

	const dateLabel = (iso: string) => {
		const key = toDateKey(iso)
		if (key === todayKey) return content.today
		if (key === yesterdayKey) return content.yesterday
		return formatPreferenceDate(iso)
	}

	return (
		<ScrollArea className='flex-1 px-4'>
			<div className='flex flex-col py-4'>
				{messages.map((message, index) => {
					const prev = messages[index - 1]
					const next = messages[index + 1]
					const clustered = shouldCluster(prev, message)
					const isLastInRun = !next || !shouldCluster(message, next)
					const showDateSeparator = !prev || toDateKey(prev.createdAt) !== toDateKey(message.createdAt)
					return (
						<div key={message.id}>
							{showDateSeparator && (
								<div className='my-4 flex items-center gap-3'>
									<div className='h-px flex-1 bg-border' />
									<span className='text-xs font-medium text-muted-foreground'>
										{dateLabel(message.createdAt)}
									</span>
									<div className='h-px flex-1 bg-border' />
								</div>
							)}
							<div className={clustered ? (isLastInRun ? 'mb-3' : 'mb-0.5') : 'mb-3'}>
								<MessageBubble
									message={message}
									own={message.from === currentUserId}
									showAuthor={!clustered}
								/>
							</div>
						</div>
					)
				})}
				<div ref={bottomRef} />
			</div>
		</ScrollArea>
	)
}

export { MessageThread }
